/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import LoadingIndicator from "../../components/LoadingIndicator";
import EmptyView from "../../components/EmptyView";
import Stack from "../../components/ui/Stack";
import Text from "../../components/ui/Text";
import Button from "../../components/ui/Button";
import Icon from "../../components/ui/Icon";
import { Tabs, TabsList, TabsItem, TabContent } from "../../components/ui/Tabs";
import WaitingListItem from "../../components/waiting-list/WaitingListItem";
import { useWaitingList } from "../../lib/react-query/useWaitingList";
import { ROUTES } from "../../constants/constants";

const UPCOMING_PARAMS = {
  "filter[upcoming_only]": 1,
  "filter[active_only]": 1,
  include: "service,provider,location,availability",
  order_field: "date",
  order_direction: "asc",
};

const HISTORY_PARAMS = {
  "filter[history_only]": 1,
  include: "service,provider,location",
  order_field: "date",
  order_direction: "desc",
};


const useLoadMore = (query) => {
  const [pagesShown, setPagesShown] = useState(1);
  const pages = query.data?.pages ?? [];

  return {
    items: pages.slice(0, pagesShown).flatMap((page) => page.data),
    hasMore: pagesShown < pages.length || !!query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage && pagesShown > pages.length,
    loadMore: () => {
      if (pagesShown >= pages.length && !query.isFetchingNextPage) query.fetchNextPage();
      setPagesShown(pagesShown + 1);
    },
  };
};

const LoadMoreButton = ({ list }) =>
  list.hasMore ? (
    <Button variant="outline" onClick={list.loadMore} loading={list.isLoadingMore} title="Load more" />
  ) : null;

const WaitingListItems = ({ items }) => items.map((entry) => <WaitingListItem key={entry.id} entry={entry} />);

const WaitingListSection = ({ title, items }) =>
  items.length > 0 ? (
    <Stack $gap="1rem">
      <Text size="large" color="dark">
        {title}
      </Text>
      <WaitingListItems items={items} />
    </Stack>
  ) : null;

const ClientWaitingList = () => {
  const navigate = useNavigate();
  const upcomingQuery = useWaitingList(UPCOMING_PARAMS);
  const upcoming = useLoadMore(upcomingQuery);
  const allUpcoming = useLoadMore(upcomingQuery);

  const isAllUpcomingComplete = upcomingQuery.isSuccess && !allUpcoming.hasMore;
  const historyQuery = useWaitingList(HISTORY_PARAMS, { enabled: isAllUpcomingComplete });
  const history = useLoadMore(historyQuery);

  const isInitialLoading = upcomingQuery.isLoading || (historyQuery.isLoading && upcoming.items.length === 0);

  const bookAppointment = (
    <Button variant="primary" title="Book an appointment" onClick={() => navigate(ROUTES.HOME)} fullWidth />
  );

  let content;
  if (isInitialLoading) {
    content = <LoadingIndicator />;
  } else if (upcoming.items.length === 0 && history.items.length === 0) {
    content = (
      <EmptyView
        icon={Icon.ClockCheck}
        title="You're not on any waiting lists"
        description="When a day is fully booked, join its waiting list. We'll email you the moment a slot opens - and you can grab it if no one takes it first."
        action={bookAppointment}
      />
    );
  } else {
    content = (
      <Tabs defaultTab="upcoming">
        <TabsList>
          <TabsItem title="Upcoming" id="upcoming" />
          <TabsItem title="All" id="all" />
        </TabsList>
        <TabContent id="upcoming">
          <Stack $gap="1rem">
            {upcoming.items.length > 0 ? (
              <WaitingListItems items={upcoming.items} />
            ) : (
              <EmptyView
                icon={Icon.ClockCheck}
                title="No active waiting list requests"
                description="Your earlier requests are on the All tab. When a day is fully booked, join its waiting list and we'll email you the moment a slot opens."
                action={bookAppointment}
              />
            )}
            <LoadMoreButton list={upcoming} />
          </Stack>
        </TabContent>
        <TabContent id="all">
          <Stack $gap="1.5rem">
            <WaitingListSection title="Upcoming" items={allUpcoming.items} />
            <WaitingListSection title="Archived" items={history.items} />
            <LoadMoreButton list={isAllUpcomingComplete ? history : allUpcoming} />
          </Stack>
        </TabContent>
      </Tabs>
    );
  }

  return (
    <Container>
      <NavigationHeader title="Waiting list" back={ROUTES.HOME} />
      {content}
    </Container>
  );
};

export default ClientWaitingList;
